<template>
  <myDialog
    :isDialog="isDialog"
    :title="displayTitle"
    :width="'1000px'"
    @handleClose="handleClose"
  >
    <template slot="content">
      <el-form
        :model="formData"
        :rules="isView ? {} : rules"
        ref="form"
        label-width="180px"
        class="box"
        v-loading="formLoading"
        element-loading-text="加载中..."
        element-loading-background="rgba(255, 255, 255, 0.72)"
      >
        <!-- 机构名称 -->
        <el-form-item label="培训活动名称" prop="name">
          <el-input
            v-model="formData.name"
            placeholder="请输入培训活动名称"
            :disabled="isView"
          ></el-input>
        </el-form-item>

        <!-- 关联机构 -->
        <el-form-item label="关联机构" prop="institutionId">
          <el-select
            v-model="formData.institutionId"
            placeholder="请选择培训机构"
            clearable
            filterable
            style="width: 100%"
            :disabled="isView"
            @change="handleInstitutionRelatedChange"
          >
            <el-option
              v-for="(item, idx) in institutionRelatedOptions"
              :key="institutionOptionKey(item, idx)"
              :label="institutionOptionLabel(item)"
              :value="institutionOptionValue(item)"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <!-- 关联培训项目 -->
        <el-form-item label="关联培训项目" prop="projectId">
          <el-select
            v-model="formData.projectId"
            placeholder="请选择培训项目"
            clearable
            filterable
            style="width: 100%"
            :disabled="isView"
            @change="handleProjectRelatedChange"
          >
            <el-option
              v-for="(item, idx) in projectRelatedOptions"
              :key="projectOptionKey(item, idx)"
              :label="projectOptionLabel(item)"
              :value="projectOptionValue(item)"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="开班时间配置" prop="openClassTimeList">
          <el-checkbox-group
            v-model="formData.openClassTimeList"
            class="open-class-time-group"
            :disabled="isView"
          >
            <div class="open-class-time-grid">
              <el-checkbox
                v-for="item in openClassTimeOptions"
                :key="item.dictValue"
                :label="item.dictValue"
              >
                {{ item.dictLabel }}
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </el-form-item>

      </el-form>
    </template>
    <template slot="footer">
      <div>
        <el-button v-if="!isView" type="primary" @click="handleSubmit" :loading="loading">保存</el-button>
        <el-button @click="handleClose">{{ isView ? "关闭" : "取消" }}</el-button>
      </div>
    </template>
  </myDialog>
</template>

<script>
import {
  getTrainingClassSignupInfo,
  updateTrainingClassSignupInfo,
  getTrainingInstitutionRelated,
  getTrainingProjectRelated,
} from "@/api/training";
import { getListByDictType } from "@/api/sys/dictData";

export default {
  name: "PolicyDialog",
  props: {
    isDialog: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isView: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: () => ({}),
    },
    /** 弹窗标题（有值则优先于默认「添加/编辑/查看活动」） */
    titleText: {
      type: String,
      default: "",
    },
  },
  computed: {
    displayTitle() {
      const t = this.titleText != null ? String(this.titleText).trim() : "";
      if (t) return t;
      if (this.isView) return "查看活动";
      if (this.isEdit) return "编辑活动";
      return "添加活动";
    },
  },
  data() {
    return {
      formData: {
        name: "",
        institutionId: "",
        institutionName: "",
        projectId: "",
        projectName: "",
        contactAddress: "",
        contactPhone: "",
        integrityGradeCode: "",
        integrityGradeName: "",
        assessYear: "",
        networkVerificationCode: "",
        networkVerificationName: "",
        verificationYear: "",
        ifSupervisorExamine: null,
        supervisorQuestion: "",
        ifSafetyExamine: null,
        safetyQuestion: "",
        licenseNumber: "",
        licenseValidStart: "",
        licenseValidEnd: "",
        cultivateInstitutionTrainingItemList: [],
        openClassTimeList: [],
      },
      licenseValidDateRange: [],
      integrityGradeOptions: [],
      networkVerificationOptions: [],
      institutionRelatedOptions: [],
      projectRelatedOptions: [],
      openClassTimeOptions: [],
      formLoading: false,
      loading: false,
      rules: {
        name: [{ required: true, whitespace: true, message: "请输入培训活动名称", trigger: "blur" }],
        institutionId: [{ required: true, message: "请选择关联机构", trigger: "change" }],
        projectId: [{ required: true, message: "请选择关联培训项目", trigger: "change" }],
        openClassTimeList: [
          {
            type: "array",
            required: true,
            min: 1,
            message: "请至少选择一项开班时间配置",
            trigger: "change",
          },
        ],
      },
    };
  },
  watch: {
    isDialog: {
      handler(val) {
        if (!val) {
          this.resetForm();
          return;
        }
        this.bootstrapDialogOpen();
      },
      immediate: true,
    },
    licenseValidDateRange(val) {
      if (val && val.length === 2) {
        // 将日期格式拼接为 yyyy-MM-dd 00:00:00
        const formatDate = (dateStr) => {
          if (!dateStr) return "";
          // 如果已经有时间部分，提取日期部分
          const datePart = dateStr.split(" ")[0];
          // 确保日期格式正确，然后拼接时间
          return datePart + " 00:00:00";
        };
        this.formData.licenseValidStart = formatDate(val[0]);
        this.formData.licenseValidEnd = formatDate(val[1]);
      } else {
        this.formData.licenseValidStart = "";
        this.formData.licenseValidEnd = "";
      }
    },
  },
  methods: {
    async bootstrapDialogOpen() {
      if (!this.isDialog) return;
      this.formLoading = true;
      try {
        await Promise.all([
          this.loadDictData(),
          this.loadRelatedInstitutionOptions(),
          this.loadRelatedProjectOptions(),
        ]);
        // 编辑 / 查看：用列表行数据回填
        if (
          (this.isEdit || this.isView) &&
          this.editData &&
          Object.keys(this.editData).length > 0
        ) {
          this.loadFormData(this.editData);
        }
      } catch (e) {
        console.error("弹窗数据加载失败", e);
        this.$message.error("数据加载失败，请稍后重试");
      } finally {
        this.formLoading = false;
      }
    },
    pickResponseRows(res) {
      const d = res && res.data;
      if (Array.isArray(d)) return d;
      if (d && Array.isArray(d.rows)) return d.rows;
      if (d && Array.isArray(d.list)) return d.list;
      if (d && Array.isArray(d.records)) return d.records;
      return [];
    },
    async loadRelatedInstitutionOptions() {
      try {
        const listQuery = { pageNum: 1, pageSize: 9999 };
        const res = await getTrainingInstitutionRelated(listQuery);
        if (res && res.code === 200) {
          this.institutionRelatedOptions = this.pickResponseRows(res);
        } else {
          this.institutionRelatedOptions = [];
        }
      } catch (e) {
        console.error("加载关联机构列表失败", e);
        this.institutionRelatedOptions = [];
      }
    },
    async loadRelatedProjectOptions() {
      try {
        const listQuery = { pageNum: 1, pageSize: 9999 };
        const res = await getTrainingProjectRelated(listQuery);
        if (res && res.code === 200) {
          this.projectRelatedOptions = this.pickResponseRows(res);
        } else {
          this.projectRelatedOptions = [];
        }
      } catch (e) {
        console.error("加载关联培训项目列表失败", e);
        this.projectRelatedOptions = [];
      }
    },
    institutionOptionValue(row) {
      const v = row.id ?? row.institutionId ?? row.cultivateInstitutionId;
      return v === undefined || v === null ? "" : v;
    },
    institutionOptionLabel(row) {
      return row.name || row.institutionName || row.cultivateInstitutionName || row.unitName || row.orgName || "-";
    },
    institutionOptionKey(row, idx) {
      const v = this.institutionOptionValue(row);
      return v !== "" && v != null ? String(v) : `inst-${idx}`;
    },
    projectOptionValue(row) {
      const v = row.id ?? row.projectId ?? row.cultivateProjectId;
      return v === undefined || v === null ? "" : v;
    },
    projectOptionLabel(row) {
      return row.projectName || row.name || row.trainingProjectName || row.title || "-";
    },
    projectOptionKey(row, idx) {
      const v = this.projectOptionValue(row);
      return v !== "" && v != null ? String(v) : `proj-${idx}`;
    },
    handleInstitutionRelatedChange(value) {
      const row = this.institutionRelatedOptions.find(
        (item) => String(this.institutionOptionValue(item)) === String(value ?? ""),
      );
      this.formData.institutionName = row ? this.institutionOptionLabel(row) : "";
    },
    handleProjectRelatedChange(value) {
      const row = this.projectRelatedOptions.find(
        (item) => String(this.projectOptionValue(item)) === String(value ?? ""),
      );
      this.formData.projectName = row ? this.projectOptionLabel(row) : "";
    },
    // 加载字典数据
    async loadDictData() {
      // 加载诚信等级
      try {
        const integrityRes = await getListByDictType("cultivate_institution_integrity_grade");
        if (integrityRes && integrityRes.code === 200 && integrityRes.data) {
          this.integrityGradeOptions = integrityRes.data;
        }
      } catch (error) {
        console.error("加载诚信等级失败", error);
      }

      // 加载网络核验
      try {
        const networkRes = await getListByDictType("cultivate_institution_network_verification");
        if (networkRes && networkRes.code === 200 && networkRes.data) {
          this.networkVerificationOptions = networkRes.data;
        }
      } catch (error) {
        console.error("加载网络核验失败", error);
      }

      try {
        const openClassRes = await getListByDictType("cultivate_activity_open_class_time");
        if (openClassRes && openClassRes.code === 200 && openClassRes.data) {
          const sorted = this.sortDictByNumericValue(openClassRes.data);
          this.openClassTimeOptions = sorted.map((row) => ({
            ...row,
            dictValue: row.dictValue !== undefined && row.dictValue !== null ? String(row.dictValue) : "",
          }));
        }
      } catch (error) {
        console.error("加载开班时间配置字典失败", error);
      }
    },
    sortDictByNumericValue(list) {
      if (!Array.isArray(list)) return [];
      return [...list].sort((a, b) => {
        const na = Number(a.dictValue);
        const nb = Number(b.dictValue);
        const aNum = a.dictValue !== "" && a.dictValue !== undefined && !Number.isNaN(na);
        const bNum = b.dictValue !== "" && b.dictValue !== undefined && !Number.isNaN(nb);
        if (aNum && bNum) return na - nb;
        if (aNum) return -1;
        if (bNum) return 1;
        return String(a.dictValue || "").localeCompare(String(b.dictValue || ""), "zh");
      });
    },
    // 加载表单数据
    loadFormData(data) {
      // 根据 integrityGradeCode 查找对应的 integrityGradeName
      let integrityGradeName = data.integrityGradeName || "";
      if (!integrityGradeName && data.integrityGradeCode) {
        const selectedOption = this.integrityGradeOptions.find((item) => item.dictValue === data.integrityGradeCode);
        if (selectedOption) {
          integrityGradeName = selectedOption.dictLabel;
        }
      }

      // 根据 networkVerificationCode 查找对应的 networkVerificationName
      let networkVerificationName = data.networkVerificationName || "";
      if (!networkVerificationName && data.networkVerificationCode) {
        const selectedOption = this.networkVerificationOptions.find(
          (item) => item.dictValue === data.networkVerificationCode,
        );
        if (selectedOption) {
          networkVerificationName = selectedOption.dictLabel;
        }
      }

      const institutionIdRaw = data.institutionId ?? data.cultivateInstitutionId;
      const institutionId =
        institutionIdRaw !== undefined && institutionIdRaw !== null && institutionIdRaw !== ""
          ? institutionIdRaw
          : "";
      let institutionName = data.institutionName || data.cultivateInstitutionName || "";
      if (!institutionName && institutionId !== "" && institutionId != null) {
        const opt = this.institutionRelatedOptions.find(
          (item) => String(this.institutionOptionValue(item)) === String(institutionId),
        );
        if (opt) institutionName = this.institutionOptionLabel(opt);
      }

      const projectIdRaw = data.projectId ?? data.cultivateProjectId ?? data.trainingProjectId;
      const projectId =
        projectIdRaw !== undefined && projectIdRaw !== null && projectIdRaw !== "" ? projectIdRaw : "";
      let projectName = data.projectName || data.trainingProjectName || "";
      if (!projectName && projectId !== "" && projectId != null) {
        const opt = this.projectRelatedOptions.find(
          (item) => String(this.projectOptionValue(item)) === String(projectId),
        );
        if (opt) projectName = this.projectOptionLabel(opt);
      }

      this.formData = {
        id: data.activityId ?? data.id,
        name: data.name || "",
        institutionId,
        institutionName,
        projectId,
        projectName,
        contactAddress: data.contactAddress || "",
        contactPhone: data.contactPhone || "",
        integrityGradeCode: data.integrityGradeCode || "",
        integrityGradeName: integrityGradeName,
        assessYear: data.assessYear || "",
        networkVerificationCode: data.networkVerificationCode || "",
        networkVerificationName: networkVerificationName,
        verificationYear: data.verificationYear || "",
        ifSupervisorExamine:
          data.ifSupervisorExamine !== undefined && data.ifSupervisorExamine !== null
            ? Number(data.ifSupervisorExamine)
            : null,
        supervisorQuestion: data.supervisorQuestion || "",
        ifSafetyExamine:
          data.ifSafetyExamine !== undefined && data.ifSafetyExamine !== null ? Number(data.ifSafetyExamine) : null,
        safetyQuestion: data.safetyQuestion || "",
        licenseNumber: data.licenseNumber || "",
        licenseValidStart: data.licenseValidStart || "",
        licenseValidEnd: data.licenseValidEnd || "",
        cultivateInstitutionTrainingItemList:
          data.cultivateInstitutionTrainingItemList && Array.isArray(data.cultivateInstitutionTrainingItemList)
            ? data.cultivateInstitutionTrainingItemList.map((item) => ({
                name: item.name || "",
                level: item.level || "",
              }))
            : [],
        openClassTimeList: this.normalizeOpenClassTimeValuesForForm(data.openClassTimeList ?? data.TimeConfigList),
      };

      // 设置许可证有效期范围
      if (this.formData.licenseValidStart && this.formData.licenseValidEnd) {
        // 从带时间的字符串中提取日期部分（yyyy-MM-dd）
        const extractDate = (dateTimeStr) => {
          if (!dateTimeStr) return "";
          // 如果包含空格，提取日期部分
          return dateTimeStr.split(" ")[0];
        };
        this.licenseValidDateRange = [
          extractDate(this.formData.licenseValidStart),
          extractDate(this.formData.licenseValidEnd),
        ];
      } else {
        this.licenseValidDateRange = [];
      }
    },
    parseOpenClassTimeRawToTokens(raw) {
      if (Array.isArray(raw)) {
        return raw.map((v) => String(v).trim()).filter(Boolean);
      }
      if (raw !== undefined && raw !== null && raw !== "") {
        return String(raw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    },
    /** 将接口回传的 value 或 label 统一成 checkbox 绑定的 dictValue */
    normalizeOpenClassTimeValuesForForm(raw) {
      const parts = this.parseOpenClassTimeRawToTokens(raw);
      const opts = this.openClassTimeOptions || [];
      return parts.map((t) => {
        const byVal = opts.find((o) => String(o.dictValue) === t);
        if (byVal) return String(byVal.dictValue);
        const byLab = opts.find((o) => String(o.dictLabel) === t);
        if (byLab) return String(byLab.dictValue);
        return t;
      });
    },
    /** 提交时把已选 dictValue 转为 dictLabel 传给后端 */
    resolveOpenClassTimeLabel(val) {
      const s = String(val);
      const opt = this.openClassTimeOptions.find((o) => String(o.dictValue) === s);
      return opt ? String(opt.dictLabel) : s;
    },
    // 处理诚信等级选择变化
    handleIntegrityGradeChange(value) {
      if (value) {
        // 根据选择的 dictValue 找到对应的 dictLabel
        const selectedOption = this.integrityGradeOptions.find((item) => item.dictValue === value);
        if (selectedOption) {
          this.formData.integrityGradeName = selectedOption.dictLabel;
        }
      } else {
        // 清空时，同时清空 name
        this.formData.integrityGradeName = "";
      }
    },
    // 处理网络核验选择变化
    handleNetworkVerificationChange(value) {
      if (value) {
        // 根据选择的 dictValue 找到对应的 dictLabel
        const selectedOption = this.networkVerificationOptions.find((item) => item.dictValue === value);
        if (selectedOption) {
          this.formData.networkVerificationName = selectedOption.dictLabel;
        }
      } else {
        // 清空时，同时清空 name
        this.formData.networkVerificationName = "";
      }
    },
    // 处理手机号输入
    handlePhoneInput(value) {
      // 只允许输入数字
      this.formData.contactPhone = value.replace(/\D/g, "");
    },
    // 添加培训项目
    handleAddTrainingItem() {
      this.formData.cultivateInstitutionTrainingItemList.push({
        name: "",
        level: "",
      });
    },
    // 删除培训项目
    handleRemoveTrainingItem(index) {
      this.formData.cultivateInstitutionTrainingItemList.splice(index, 1);
    },
    // 提交
    handleSubmit() {
      if (this.isView) {
        this.handleClose();
        return;
      }
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.loading = true;

          const openClassTimeList = (this.formData.openClassTimeList || []).map((v) =>
            this.resolveOpenClassTimeLabel(v),
          );
          const submitData = {
            name: String(this.formData.name || "").trim(),
            institutionId: this.formData.institutionId,
            projectId: this.formData.projectId,
            openClassTimeList,
          };

          const api = this.isEdit && this.formData.id ? updateTrainingClassSignupInfo : getTrainingClassSignupInfo;
          if (this.isEdit && this.formData.id) {
            submitData.id = this.formData.id;
          }

          api(submitData)
            .then((res) => {
              if (res && res.code === 200) {
                this.$message.success(this.isEdit ? "编辑成功" : "新增成功");
                this.$emit("success");
                this.handleClose();
              } else {
                this.$message.error(res.msg || "操作失败");
              }
            })
            .catch((err) => {
              console.error("请求出错", err);
              this.$message.error("操作失败");
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    // 关闭
    handleClose() {
      this.$emit("handleClose");
      this.resetForm();
    },
    // 重置表单
    resetForm() {
      this.$refs.form && this.$refs.form.resetFields();
      this.formData = {
        name: "",
        institutionId: "",
        institutionName: "",
        projectId: "",
        projectName: "",
        contactAddress: "",
        contactPhone: "",
        integrityGradeCode: "",
        integrityGradeName: "",
        assessYear: "",
        networkVerificationCode: "",
        networkVerificationName: "",
        verificationYear: "",
        ifSupervisorExamine: null,
        supervisorQuestion: "",
        ifSafetyExamine: null,
        safetyQuestion: "",
        licenseNumber: "",
        licenseValidStart: "",
        licenseValidEnd: "",
        cultivateInstitutionTrainingItemList: [],
        openClassTimeList: [],
      };
      this.licenseValidDateRange = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.training-item-list {
  width: 100%;

  .el-table {
    margin-bottom: 10px;
  }
}

/deep/ .el-form-item__label {
  .el-form-item.is-required &::before {
    content: "*";
    color: #f56c6c;
    margin-right: 4px;
  }
}

/deep/ .el-form-item__label {
  color: #2a3347 !important;
  font-weight: 400;
  font-size: 16px;
}

/deep/ .el-dialog__header {
  background: #297dee !important;
}

/deep/ .el-dialog__title {
  color: #ffffff !important;
  font-weight: 700;
  font-size: 17px;
}

/deep/ .el-dialog__headerbtn {
  filter: brightness(0) invert(1) !important;
}
.box {
  height: 500px;
  background: white;
}

.open-class-time-group {
  width: 100%;
}

.open-class-time-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px 16px;
  align-items: center;
}
</style>
